import { BuildOutlined } from '@ant-design/icons';

import { Flex } from '@/components/base';
import TooltipV2 from '@/components/Tooltip/TooltipV2';
import PropertyValue from '@/components/Typography/PropertyValue';
import Typo from '@/components/Typography/Typo';
import { NOT_ASSIGNED_LABEL } from '@/configs/reserved-word';
import taxonomyUtils from '@/features/Taxonomy/utils/taxonomy';
import useColor from '@/hooks/domain/useColor';
import { isEmptyArray } from '@/utils/util';

import { ValueWrapper } from '../ValueWrapper';
import { PropertyRowWrapper } from './PropertyRowWrapper';
import type { CommonFieldProps } from './types';

export const TopicField = (
  props: CommonFieldProps & {
    topic2_list?: TopicWithTaxonomyName[];
    setShowFull?: (showFull: boolean) => void;
  },
) => {
  if (!props.showFull && isEmptyArray(props.topic2_list)) return null;

  return (
    <PropertyRowWrapper {...props} expandable>
      <Flex w="wrap" g={6} f={1}>
        {props.showFull ? (
          <UnfoldedTopicList topics={props.topic2_list || []} />
        ) : (
          <FoldedTopicList
            topics={props.topic2_list || []}
            setShowFull={props.setShowFull}
          />
        )}
      </Flex>
    </PropertyRowWrapper>
  );
};

const FoldedTopicList = (props: {
  topics: TopicWithTaxonomyName[];
  setShowFull?: (showFull: boolean) => void;
}) => {
  const color = useColor();
  const MAX_TOPIC_COUNT = 2;
  const slicedTopics = props.topics.slice(0, MAX_TOPIC_COUNT);

  return (
    <div className="flex gap-1 w-full overflow-ellipsis">
      {slicedTopics.map((topic) => {
        const paths = taxonomyUtils.getTopicPath(topic).map((t) => t.label);
        return (
          <ValueWrapper key={topic.alias} className="w-fit">
            <TooltipV2 tooltipNode={paths.join('/') + ' / ' + topic.name}>
              <Flex g={1} className="items-start">
                <Flex className="min-w-4 pt-[1px]">
                  <BuildOutlined
                    style={{ fontSize: 12, color: color.gray600 }}
                  />
                </Flex>
                <Typo type="label3" ellipsis className="max-w-30 w-fit">
                  {topic.name}
                </Typo>
              </Flex>
            </TooltipV2>
          </ValueWrapper>
        );
      })}
      {props.topics.length > MAX_TOPIC_COUNT && (
        <TooltipV2
          tooltipNode={`Show ${props.topics.length - MAX_TOPIC_COUNT} more`}
        >
          <div className="min-w-fit">
            <ValueWrapper onClick={() => props.setShowFull?.(true)}>
              <Typo type="label3" className="text-gray-500">
                +{props.topics.length - MAX_TOPIC_COUNT} more
              </Typo>
            </ValueWrapper>
          </div>
        </TooltipV2>
      )}
    </div>
  );
};

const UnfoldedTopicList = (props: { topics: TopicWithTaxonomyName[] }) => {
  const color = useColor();
  return (
    <div className="flex flex-wrap gap-1">
      {props.topics.map((topic) => {
        const paths = taxonomyUtils.getTopicPath(topic).map((t) => t.label);
        return (
          <ValueWrapper key={topic.alias}>
            <Flex g={1} className="items-start">
              <Flex className="min-w-4 pt-[3px]">
                <BuildOutlined style={{ fontSize: 12, color: color.gray600 }} />
              </Flex>
              <Typo type="body3">
                {paths.join('/') + ' / '}
                {topic.name}
              </Typo>
            </Flex>
          </ValueWrapper>
        );
      })}
      {props.topics.length === 0 && (
        <PropertyValue placeholder={NOT_ASSIGNED_LABEL} />
      )}
    </div>
  );
};
